import React, { useState, useEffect, useCallback } from 'react';
import { Box, TextField, Button, Typography, Paper, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationModal from '../components/ConfirmationModal';

const CronoanaliseEtapas = ({
  etapa,
  objCronoanalise,
  eventoTeclado,
  onIniciarCronometragem,
  onNovaCronometragem,
  onFinalizarCronometragem,
  isRecording,
  elapsedTime,
  formatTime,
  videoRef,
  recordingMode,
  startRecording,
  stopRecording,
  registrarPrepara,
  preparaTimes,
  loading,
  cameraAvailable,
  setPreparaTimes,
  processoId,
  CronometroService
}) => {
  const usuarioLogado = sessionStorage.getItem("usuarioNome") || "";
  const [openDialog, setOpenDialog] = useState(false);
  const [novaOperacao, setNovaOperacao] = useState("");
  const [operacoes, setOperacoes] = useState(() => {
    const saved = localStorage.getItem('operacoes');
    return saved ? JSON.parse(saved) : [];
  });
  const [tempoInicial, setTempoInicial] = useState(0);
  const [selectedTimes, setSelectedTimes] = useState([]);
  
  // Modal de confirmação
  const [modalConfirm, setModalConfirm] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null,
    confirmText: 'Confirmar',
    cancelText: 'Cancelar'
  });

  // Atualiza o tempo inicial quando começar a gravar
  useEffect(() => {
    if (isRecording && elapsedTime === 0) {
      setTempoInicial(0);
    }
  }, [isRecording, elapsedTime]);

  // Função para adicionar nova operação
  const handleAdicionarOperacao = () => {
    if (novaOperacao.trim()) {
      const novasOperacoes = [...operacoes, novaOperacao.trim()];
      setOperacoes(novasOperacoes);
      localStorage.setItem('operacoes', JSON.stringify(novasOperacoes));
      setNovaOperacao("");
      setOpenDialog(false);
    }
  };

  // Calcula o tempo desde a última marcação
  const getTempoAtual = () => {
  if (!isRecording) return 0;
  
  // Retorna a diferença entre o tempo atual e o tempoInicial
  return Math.floor(elapsedTime - tempoInicial);
};

  // Função para marcar tempo
  const handleMarcarTempo = async () => {
  if (!isRecording) return;
  
  try {
    // Obter o tempo absoluto do backend (em segundos)
    const tempoAtualSegundos = await CronometroService.getTempo(processoId);
    const tempoAtualMs = Math.floor(tempoAtualSegundos * 1000); // Converter para ms com valores inteiros
    
    // Calcular tempo decorrido desde o último reset
    const tempoDecorridoMs = tempoAtualMs - tempoInicial;
    
    // Registrar o novo tempo (em milissegundos)
    const novoTempo = {
      id: Date.now(),
      time: tempoDecorridoMs,
      selected: true
    };
    
    setPreparaTimes(prev => [...prev, novoTempo]);
    
    // Resetar o tempoInicial para o tempo absoluto atual
    setTempoInicial(tempoAtualMs);
    
  } catch (error) {
    console.error("Erro ao marcar tempo:", error);
    // Adicione feedback visual para o usuário
  }
};

  // Função para excluir tempos selecionados
  const handleExcluirTempos = () => {
    setPreparaTimes(prev => prev.filter(time => !selectedTimes.includes(time.id)));
    setSelectedTimes([]);
  };

  // Função para alternar seleção de tempo
  const toggleTimeSelection = useCallback((timeId) => {
    setSelectedTimes(prev => 
      prev.includes(timeId) 
        ? prev.filter(id => id !== timeId)
        : [...prev, timeId]
    );
  }, []);

  // Função para cancelar todo o processo
  const handleCancelar = () => {
    setModalConfirm({
      open: true,
      title: 'Cancelar Cronoanálise',
      message: 'Deseja realmente cancelar todo o processo?',
      type: 'warning',
      confirmText: 'Sim, Cancelar',
      cancelText: 'Voltar',
      onConfirm: () => {
        setModalConfirm(prev => ({ ...prev, open: false }));
        setPreparaTimes([]);
        onFinalizarCronometragem();
      }
    });
  };

  if (etapa === 1) {
    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Dados Iniciais da Cronoanálise
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <DatePicker
            label="Data"
            value={objCronoanalise.data}
            onChange={(newValue) => eventoTeclado({ target: { name: 'data', value: newValue } })}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
          
          <TextField
            label="Produto/Modelo"
            name="modelo"
            value={objCronoanalise.modelo}
            onChange={eventoTeclado}
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            disabled
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#000000',
                color: '#000000'
              }
            }}
          />
          
          <TextField
            label="Setor"
            name="setor"
            value={objCronoanalise.setor}
            onChange={eventoTeclado}
            fullWidth
          />
          
          <TextField
            label="Cronometrista"
            name="cronometrista"
            value={usuarioLogado}
            disabled
            fullWidth
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onIniciarCronometragem}
            fullWidth
          >
            Iniciar Cronometragem
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCancelar}
            fullWidth
          >
            Cancelar
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="h5" gutterBottom>
        Cronometragem de Operação
      </Typography>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Coluna da esquerda - Formulário */}
        <Box sx={{ flex: '0 0 300px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Select
              label="Operação"
              name="operacao"
              value={objCronoanalise.operacao}
              onChange={eventoTeclado}
              fullWidth
              displayEmpty
            >
              <MenuItem value="" disabled>
                Selecione uma operação
              </MenuItem>
              {operacoes.map((op, index) => (
                <MenuItem key={index} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOpenDialog(true)}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              Nova
            </Button>
          </Box>

          <TextField
            label="Operador"
            name="operador"
            value={objCronoanalise.operador}
            onChange={eventoTeclado}
            fullWidth
            size="small"
          />

          <TextField
            label="Máquina/Posto"
            name="maqPosto"
            value={objCronoanalise.maqPosto}
            onChange={eventoTeclado}
            fullWidth
            size="small"
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Freq. Lógica (N)"
              name="freqLogicaNumerador"
              value={objCronoanalise.freqLogicaNumerador}
              onChange={eventoTeclado}
              type="number"
              size="small"
              sx={{ flex: 1 }}
            />
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>/</Typography>
          <TextField
              label="Freq. Lógica (D)"
              name="freqLogicaDenominador"
              value={objCronoanalise.freqLogicaDenominador}
            onChange={eventoTeclado}
              type="number"
              size="small"
              sx={{ flex: 1 }}
          />
          </Box>

          <TextField
            label="Eficiência (%)"
            name="eficiencia"
            value={objCronoanalise.eficiencia}
            onChange={eventoTeclado}
            fullWidth
            type="number"
            size="small"
            InputProps={{
              inputProps: { min: 0, max: 100 }
            }}
          />

          <TextField
            label="Tolerância (%)"
            name="tolerancia"
            value={objCronoanalise.tolerancia}
            onChange={eventoTeclado}
            fullWidth
            type="number"
            size="small"
            InputProps={{
              inputProps: { min: 0, max: 100 }
            }}
          />

          <TextField
            label="Produtividade Hora"
            name="produtividadeHoraInf"
            value={objCronoanalise.produtividadeHoraInf}
            onChange={eventoTeclado}
            fullWidth
            type="number"
            size="small"
            InputProps={{
              inputProps: { min: 0 }
            }}
          />

          <TextField
            label="Produtividade Dia"
            name="produtividadeDiaInf"
            value={objCronoanalise.produtividadeDiaInf}
            onChange={eventoTeclado}
            fullWidth
            type="number"
            size="small"
            InputProps={{
              inputProps: { min: 0 }
            }}
          />

          <TextField
            label="Tempo Disponível (min/dia)"
            name="tempoDisponivelDias"
            value={objCronoanalise.tempoDisponivelDias}
            onChange={eventoTeclado}
            fullWidth
            type="number"
            size="small"
            InputProps={{
              inputProps: { min: 0 }
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            {!isRecording ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => startRecording('video')}
                  disabled={loading || !cameraAvailable}
                  fullWidth
                >
                  Gravar Vídeo
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => startRecording('timer')}
                  disabled={loading}
                  fullWidth
                >
                  Apenas Cronômetro
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={stopRecording}
                fullWidth
              >
                Parar {recordingMode === 'video' ? 'Gravação' : 'Cronômetro'}
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={onNovaCronometragem}
              disabled={loading || preparaTimes.length === 0}
              fullWidth
            >
              Nova Cronometragem
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancelar}
              fullWidth
            >
              Cancelar
            </Button>
          </Box>
        </Box>

        {/* Coluna da direita - Vídeo e Tempos */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {recordingMode === 'video' && (
            <Box sx={{ 
              width: '100%', 
              height: '60vh',
              backgroundColor: '#000',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              borderRadius: 1
            }}>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: isRecording ? 'block' : 'none'
                }}
              />
              {!isRecording && (
                <Typography color="white" variant="body2">
                  Área de visualização da câmera
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 2,
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1
          }}>
            <Typography variant="h6" align="center">
              Tempo Atual: {formatTime(getTempoAtual())}
            </Typography>

            <Button
              variant="contained"
              onClick={handleMarcarTempo}
              disabled={!isRecording}
              fullWidth
            >
              Marcar Tempo
            </Button>

            {preparaTimes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Tempos registrados:
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleExcluirTempos}
                    disabled={selectedTimes.length === 0}
                    startIcon={<DeleteIcon />}
                  >
                    Excluir Selecionados
                  </Button>
                </Box>

                {preparaTimes.map((time, index) => (
                  <Box 
                    key={time.id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      p: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <Checkbox
                      checked={selectedTimes.includes(time.id)}
                      onChange={() => toggleTimeSelection(time.id)}
                    />
                    <Typography sx={{ flex: 1 }}>
                      Tempo {index + 1}: {formatTime(time.time)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Dialog para adicionar nova operação */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Adicionar Nova Operação</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Descrição da Operação"
            fullWidth
            value={novaOperacao}
            onChange={(e) => setNovaOperacao(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleAdicionarOperacao} variant="contained" color="primary">
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação */}
      <ConfirmationModal
        open={modalConfirm.open}
        title={modalConfirm.title}
        message={modalConfirm.message}
        type={modalConfirm.type}
        confirmText={modalConfirm.confirmText}
        cancelText={modalConfirm.cancelText}
        onConfirm={() => {
          modalConfirm.onConfirm && modalConfirm.onConfirm();
        }}
        onCancel={() => setModalConfirm(prev => ({ ...prev, open: false }))}
      />
    </Paper>
  );
};

export default CronoanaliseEtapas; 